// MARK://dhk8/admin-shell/pages/modules/PurchaseOrderInboxPage
// Bandeja del proveedor — solicitudes de compra entrantes que llegaron de otra
// entidad vía el puente cross-entity (dhk8_network.NetworkInboxItem).
//
// Flujo 3: SAVIs Coffee envía una OC → el puente la deposita aquí (en el tenant
// del proveedor) → el proveedor pulsa "Autorizar" → se emite el evento de vuelta
// y la OC del comprador pasa a APPROVED.
//
// Para verla hay que estar en el CONTEXTO del tenant proveedor (switch de tenant
// del admin). En el contexto del comprador la bandeja sale vacía — es correcto.
import { useState } from 'react'
import { Inbox, CheckCircle2, ArrowLeft } from 'lucide-react'
import { Link } from 'react-router-dom'
import * as po from '../../lib/clients/purchase-orders.js'
import { useAsync, asyncState } from '../../lib/use-async.js'
import { useAuthStore } from '../../stores/auth-store.js'
import { mxn } from '../../lib/money.js'

const INBOX_BADGE: Record<string, string> = {
  PENDING: 'adm-badge--warn',
  APPROVED: 'adm-badge--ok',
  REJECTED: 'adm-badge--danger',
}
const INBOX_LABEL: Record<string, string> = {
  PENDING: 'Pendiente',
  APPROVED: 'Autorizada',
  REJECTED: 'Rechazada',
}

export default function PurchaseOrderInboxPage() {
  const list = useAsync((s) => po.listNetworkInbox(s))
  const session = useAuthStore((s) => s.session)

  const [busyId, setBusyId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function authorize(item: po.InboxItem) {
    if (!item.payload.poId) { setError('La solicitud no trae un poId — no se puede autorizar.'); return }
    setError(null); setBusyId(item.id)
    try {
      await po.authorizeNetworkRequest({
        inboxItemId: item.id,
        poId: item.payload.poId,
        ...(item.payload.networkId ? { networkId: item.payload.networkId } : {}),
        approvedBy: session?.fullName ?? 'proveedor',
      })
      list.reload()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo autorizar la solicitud.')
    } finally { setBusyId(null) }
  }

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div>
          <Link to="/purchase-orders" className="adm-btn adm-btn--ghost adm-btn--sm" style={{ marginBottom: 6 }}><ArrowLeft size={15} /> Órdenes de compra</Link>
          <h1 className="adm-page__title">Bandeja del proveedor</h1>
          <p className="adm-page__sub">Solicitudes de compra entrantes de otras entidades</p>
        </div>
      </div>

      <div className="adm-banner" role="status" style={{ marginBottom: 12 }}>
        Esta bandeja muestra las solicitudes que otra entidad te envió. Para verlas debes estar en el
        contexto de tu tenant proveedor. Al <strong>autorizar</strong>, la orden del comprador pasa a APROBADA.
      </div>

      {asyncState(list.loading, list.error, list.reload)}
      {error && <div className="adm-banner adm-banner--danger" role="alert" style={{ marginBottom: 12 }}>{error}</div>}

      {list.data && <div className="adm-card adm-card--pad0"><div className="adm-list">
        {list.data.length === 0 && <div className="adm-empty"><Inbox size={28} aria-hidden style={{ opacity: 0.4, marginBottom: 8 }} /><div>Sin solicitudes entrantes.</div></div>}
        {list.data.map((item) => {
          const p = item.payload
          const lineCount = p.lines?.length ?? 0
          return (
            <div key={item.id} className="adm-row">
              <div className="adm-row__main">
                <div className="adm-row__title">
                  <Inbox size={14} aria-hidden style={{ verticalAlign: 'middle', marginRight: 6, opacity: 0.6 }} />
                  Solicitud de compra {p.poId ? p.poId.slice(0, 8).toUpperCase() : item.id.slice(0, 8).toUpperCase()}
                </div>
                <div className="adm-row__meta">
                  {lineCount} línea(s) · {mxn(p.totalCents ?? 0)}
                  {p.submittedAt ? ` · ${new Date(p.submittedAt).toLocaleString('es-MX')}` : ''}
                  {p.fromTenantId ? ` · de ${p.fromTenantId.slice(0, 8)}` : ''}
                </div>
              </div>
              <div className="adm-row__tail">
                <span className={`adm-badge ${INBOX_BADGE[item.status] ?? 'adm-badge--info'}`}>{INBOX_LABEL[item.status] ?? item.status}</span>
                {item.status === 'PENDING' && (
                  <button className="adm-btn adm-btn--primary adm-btn--sm" onClick={() => authorize(item)} disabled={busyId === item.id}>
                    <CheckCircle2 size={15} /> {busyId === item.id ? 'Autorizando…' : 'Autorizar'}
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div></div>}
    </div>
  )
}
